import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, PersonSimpleHike, Dog, MapPin, Mountains, Coffee, WhatsappLogo, PlusCircle, Binoculars, Sun, CloudRain, BabyCarriage, Compass, ArrowUp } from "@phosphor-icons/react";
import { toast } from "sonner";

// Fresh lake + woodland palette — pine ink, misty water page, fern + amber accents
const PAGE = "#E8F0EE";       // pale mint/lake water
const PAGE_LIGHT = "#F5F9F8";
const INK = "#14281F";         // deep pine
const INK_SOFT = "#3A4F43";
const LAKE = "#2E6B7A";        // deep lake teal — primary
const LAKE_DEEP = "#1E4A57";
const FERN = "#4A8F5A";        // fresh fern green
const AMBER = "#D98E2C";       // sun-glint amber
const RULE = "#C9D9D4";

// Map palette — OS style
const MAP_GRASS = "#E8F1D6";
const MAP_GRASS_DARK = "#D4E4B2";
const MAP_WOOD = "#9EC585";
const MAP_WOOD_DARK = "#77A35D";
const MAP_WATER = "#A9D0DA";
const MAP_WATER_DEEP = "#7AB6C6";
const MAP_CONTOUR = "#BFA47A";
const MAP_ROAD = "#F3DBB4";
const MAP_FOOTPATH = "#C73E2A";

const WALKS = [
  {
    id: "w1",
    title: "Holborough Lakes — the long loop",
    date: "Sun 28 Apr",
    time: "10:00",
    distance: "6.8 km",
    elevation: "85 m",
    terrain: "Mostly flat · some mud in the woods",
    meet: "Lake pavilion car park · HR2 8XL",
    pubEnd: "The Plough · soup & a pint",
    dogFriendly: true,
    wheels: false,
    weather: { icon: Sun, label: "Warm, breezy" },
    coming: ["m1", "m2", "m3", "me", "m5"],
    maybe: ["m6"],
    mapStyle: "lake",
    pins: [
      { x: 14, y: 64, label: "Lake pavilion" },
      { x: 68, y: 24, label: "Folly · halfway" },
      { x: 88, y: 66, label: "The Plough" },
    ],
    // Route waypoints around the lake (SVG coords on the 100x70 map)
    route: "M 14,64 Q 18,54 25,48 L 34,40 Q 42,32 52,30 L 62,26 Q 70,22 78,26 L 85,34 Q 92,44 90,54 L 88,62 Q 87,65 88,66",
  },
  {
    id: "w2",
    title: "Birchwood Ridge — morning amble",
    date: "Sun 5 May",
    time: "09:30",
    distance: "4.2 km",
    elevation: "140 m",
    terrain: "One solid climb, then gentle",
    meet: "Church Lane lay-by · HR3 1AB",
    pubEnd: "Birchwood Tea Rooms",
    dogFriendly: true,
    wheels: false,
    weather: { icon: CloudRain, label: "Possible showers" },
    coming: ["m1", "m3", "me"],
    maybe: ["m2", "m5"],
    mapStyle: "woods",
    pins: [
      { x: 14, y: 60, label: "Start · Church Lane" },
      { x: 55, y: 20, label: "Top of ridge" },
      { x: 88, y: 48, label: "Tea Rooms" },
    ],
    route: "M 14,60 L 22,50 Q 32,38 42,28 L 55,20 Q 66,24 74,32 L 82,40 L 88,48",
  },
  {
    id: "w3",
    title: "Riverside to Old Mill · flat 5k",
    date: "Sun 12 May",
    time: "10:30",
    distance: "5.0 km",
    elevation: "10 m",
    terrain: "Towpath, entirely flat",
    meet: "Riverside Cafe · HR2 6JD",
    pubEnd: "Old Mill · cream teas",
    dogFriendly: true,
    wheels: true,
    weather: { icon: Sun, label: "Forecast fair" },
    coming: ["m2", "m5", "m6"],
    maybe: [],
    mapStyle: "river",
    pins: [
      { x: 10, y: 46, label: "Riverside Cafe" },
      { x: 92, y: 46, label: "Old Mill" },
    ],
    route: "M 10,46 Q 25,44 40,48 T 68,46 Q 80,44 92,46",
  },
];

const MEMBERS = [
  { id: "m1", name: "Priya", color: LAKE_DEEP },
  { id: "m2", name: "Jim", color: AMBER },
  { id: "m3", name: "Hannah", color: FERN },
  { id: "me", name: "You", color: "#3A5F8A" },
  { id: "m5", name: "Anita", color: "#8E4A3B" },
  { id: "m6", name: "Lola", color: "#5E6F7A" },
];

export default function WalkClub() {
  const [walks, setWalks] = useState(WALKS);
  const [activeId, setActiveId] = useState("w1");
  const navigate = useNavigate();

  const member = (id) => MEMBERS.find((m) => m.id === id);
  const active = walks.find((w) => w.id === activeId);

  const rsvp = (status) => {
    setWalks((ws) => ws.map((w) => {
      if (w.id !== activeId) return w;
      const coming = w.coming.filter((x) => x !== "me");
      const maybe = w.maybe.filter((x) => x !== "me");
      if (status === "going") return { ...w, coming: [...coming, "me"], maybe };
      if (status === "maybe") return { ...w, coming, maybe: [...maybe, "me"] };
      return { ...w, coming, maybe };
    }));
    toast.success(status === "going" ? "See you Sunday!" : status === "maybe" ? "Marked as maybe" : "Marked as can't make it");
  };

  const myStatus = active.coming.includes("me") ? "going" : active.maybe.includes("me") ? "maybe" : "declined";

  return (
    <div className="min-h-screen" style={{ backgroundColor: PAGE, color: INK, fontFamily: "'Manrope', sans-serif" }} data-testid="walk-club-interactive">
      <header className="border-b" style={{ backgroundColor: PAGE_LIGHT, borderColor: RULE }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/demos/walk-club" className="opacity-50" style={{ color: INK }}><ArrowLeft size={18} /></Link>
            <div className="h-9 w-9 flex items-center justify-center" style={{ backgroundColor: LAKE }}>
              <PersonSimpleHike size={16} weight="fill" color={PAGE_LIGHT} />
            </div>
            <div>
              <div className="text-base font-bold leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>Holborough Walk Club <span className="font-mono text-[10px] opacity-60 font-normal">· Concept mock</span></div>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mt-0.5">Sunday mornings · 6 regulars</div>
            </div>
          </div>
          <button onClick={() => navigate("/consultation?demo=walk-club")} className="text-xs font-semibold px-3 py-2 border" style={{ borderColor: LAKE, color: LAKE }} data-testid="walk-book-cta">Talk to us about this</button>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Walks list */}
        <aside className="lg:col-span-1">
          <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-3">Upcoming walks</p>
          <ul className="space-y-2" data-testid="walk-list">
            {walks.map((w) => {
              const isActive = w.id === activeId;
              return (
                <li key={w.id}>
                  <button onClick={() => setActiveId(w.id)} className="w-full text-left p-4 border-2 transition-all" style={{ borderColor: isActive ? LAKE : RULE, backgroundColor: isActive ? LAKE : "white", color: isActive ? PAGE_LIGHT : INK }} data-testid={`walk-list-${w.id}`}>
                    <div className="text-[10px] font-mono uppercase tracking-widest opacity-80 mb-1">{w.date} · {w.time}</div>
                    <div className="text-sm font-bold leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{w.title}</div>
                    <div className="mt-2 flex items-center gap-3 text-[10px] opacity-80">
                      <span>{w.distance}</span>
                      <span>· {w.elevation}</span>
                      {w.dogFriendly && <Dog size={12} weight="fill" />}
                    </div>
                  </button>
                </li>
              );
            })}
            <li>
              <button className="w-full p-4 border-2 border-dashed flex items-center justify-center gap-2 text-sm font-semibold" style={{ borderColor: RULE, color: LAKE }} onClick={() => toast.message("Future work — design in playbook")} data-testid="walk-add">
                <PlusCircle size={14} weight="fill" /> Suggest a walk
              </button>
            </li>
          </ul>
        </aside>

        {/* Walk detail */}
        <div className="lg:col-span-2">
          <ActiveWalk walk={active} member={member} myStatus={myStatus} rsvp={rsvp} />
        </div>
      </section>

      <footer className="border-t mt-8 py-10" style={{ backgroundColor: PAGE_LIGHT, borderColor: RULE }}>
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-3">Concept · boots-on-the-ground, not an app full of noise</p>
          <Link to="/consultation?demo=walk-club" className="inline-flex items-center gap-2 text-white px-5 py-3 text-sm font-semibold" style={{ backgroundColor: LAKE }}>Book a 30-min consultation <ArrowRight size={14} weight="bold" /></Link>
        </div>
      </footer>
    </div>
  );
}

function ActiveWalk({ walk, member, myStatus, rsvp }) {
  const WIcon = walk.weather.icon;
  const comingCount = walk.coming.length;
  const maybeCount = walk.maybe.length;

  return (
    <div data-testid="walk-active">
      <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">{walk.date} · {walk.time}</p>
      <h1 className="text-4xl font-black tracking-tighter leading-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{walk.title}</h1>
      <p className="text-sm opacity-70 mb-5">{walk.terrain}</p>

      {/* Route map — OS-style authentic */}
      <div className="relative mb-3 border-2 overflow-hidden" style={{ borderColor: INK_SOFT }} data-testid="walk-map-container">
        <RouteMap walk={walk} />
      </div>
      {/* Map legend — numbered pin key */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6 text-xs" data-testid="walk-map-legend">
        <div className="flex items-center gap-1.5 opacity-70">
          <span className="inline-block w-5 h-[2px]" style={{ backgroundColor: MAP_FOOTPATH, backgroundImage: `repeating-linear-gradient(90deg, ${MAP_FOOTPATH} 0 4px, transparent 4px 7px)` }} />
          <span className="font-mono uppercase tracking-widest text-[10px]">Footpath</span>
        </div>
        {walk.pins.map((p, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border-2 text-[10px] font-black" style={{ borderColor: INK, backgroundColor: "white", color: INK }}>{i + 1}</span>
            <span className="font-medium">{p.label}</span>
          </div>
        ))}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="walk-stats">
        <Stat icon={Mountains} label="Distance" value={walk.distance} />
        <Stat icon={ArrowUp} label="Climb" value={walk.elevation} />
        <Stat icon={Binoculars} label="Terrain" value={walk.terrain.split("·")[0].trim()} />
        <Stat icon={WIcon} label="Weather" value={walk.weather.label} />
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-6">
        {walk.dogFriendly && <Badge icon={Dog} label="Dog-friendly" color={FERN} />}
        {walk.wheels && <Badge icon={BabyCarriage} label="Buggy / wheelchair ok" color={FERN} />}
        <Badge icon={Coffee} label={walk.pubEnd} color={AMBER} />
        <Badge icon={MapPin} label={walk.meet} color={LAKE} />
      </div>

      {/* RSVP */}
      <div className="border p-5 mb-6 bg-white" style={{ borderColor: RULE }} data-testid="walk-rsvp">
        <div className="flex items-baseline justify-between mb-3">
          <p className="text-[10px] font-mono uppercase tracking-widest opacity-60">{comingCount} coming · {maybeCount} maybe</p>
          <button onClick={() => toast.success("Copied · paste in the WhatsApp")} className="text-xs font-semibold px-3 py-1.5 text-white flex items-center gap-1" style={{ backgroundColor: "#25D366" }} data-testid="walk-share-wa"><WhatsappLogo size={12} weight="fill" /> Share</button>
        </div>
        <div className="flex gap-2 mb-4">
          {[
            { k: "going", label: "I'm coming", c: FERN },
            { k: "maybe", label: "Maybe", c: AMBER },
            { k: "declined", label: "Can't this week", c: INK_SOFT },
          ].map((o) => (
            <button key={o.k} onClick={() => rsvp(o.k)} className="flex-1 py-3 text-sm font-semibold border-2" style={{ borderColor: myStatus === o.k ? o.c : RULE, backgroundColor: myStatus === o.k ? o.c : "white", color: myStatus === o.k ? "white" : INK }} data-testid={`walk-rsvp-${o.k}`}>
              {o.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 pt-3 border-t" style={{ borderColor: RULE }}>
          {walk.coming.map((id) => {
            const m = id === "me" ? { name: "You", color: "#3A5F8A" } : member(id);
            return (
              <div key={id} className="flex items-center gap-1.5 text-xs">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-[10px]" style={{ backgroundColor: m.color }}>{m.name[0]}</div>
                <span>{m.name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Authentic-looking OS-style map. Each walk has a different base (lake / woods / river)
 * but they share the same coordinate system (0-100 x 0-70) so pins/route line up.
 */
function RouteMap({ walk }) {
  return (
    <svg viewBox="0 0 100 70" className="w-full block" preserveAspectRatio="xMidYMid slice" style={{ aspectRatio: "100 / 70", backgroundColor: MAP_GRASS }} data-testid="walk-map">
      <defs>
        <pattern id="wood-pattern" width="6" height="6" patternUnits="userSpaceOnUse">
          <rect width="6" height="6" fill={MAP_WOOD} />
          <circle cx="1.5" cy="1.5" r="0.8" fill={MAP_WOOD_DARK} opacity="0.6" />
          <circle cx="4.5" cy="4.5" r="0.8" fill={MAP_WOOD_DARK} opacity="0.6" />
          <circle cx="3" cy="2.5" r="0.6" fill={MAP_WOOD_DARK} opacity="0.4" />
        </pattern>
        <pattern id="water-pattern" width="8" height="4" patternUnits="userSpaceOnUse">
          <rect width="8" height="4" fill={MAP_WATER} />
          <path d="M 0 2 Q 2 1 4 2 T 8 2" stroke={MAP_WATER_DEEP} strokeWidth="0.2" fill="none" opacity="0.5" />
        </pattern>
        <pattern id="field-hatch" width="10" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="10" stroke={MAP_GRASS_DARK} strokeWidth="0.25" opacity="0.5" />
        </pattern>
      </defs>

      {/* Field hatching as base subtle texture */}
      <rect x="0" y="0" width="100" height="70" fill="url(#field-hatch)" opacity="0.6" />

      {/* Terrain — differs per walk */}
      {walk.mapStyle === "lake" && <LakeTerrain />}
      {walk.mapStyle === "woods" && <WoodsTerrain />}
      {walk.mapStyle === "river" && <RiverTerrain />}

      {/* Roads — shared thin lane */}
      <path d="M -2 68 Q 20 66 40 64 T 80 62 L 102 60" stroke={MAP_ROAD} strokeWidth="1.6" fill="none" />
      <path d="M -2 68 Q 20 66 40 64 T 80 62 L 102 60" stroke={INK_SOFT} strokeWidth="0.25" fill="none" strokeDasharray="0.6 0.4" opacity="0.5" />

      {/* Dashed red footpath (OS standard) */}
      <path d={walk.route} stroke="white" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <path d={walk.route} stroke={MAP_FOOTPATH} strokeWidth="0.7" fill="none" strokeLinecap="round" strokeDasharray="1.4 0.9" />

      {/* Waypoint pins — numbered OS-style */}
      {walk.pins.map((p, i) => (
        <g key={i} transform={`translate(${p.x}, ${p.y})`}>
          <circle r="2.6" fill="white" stroke={INK} strokeWidth="0.6" />
          <text y="0.9" textAnchor="middle" fontFamily="'Outfit', sans-serif" fontSize="3" fontWeight="900" fill={INK}>{i + 1}</text>
        </g>
      ))}

      {/* Compass rose */}
      <g transform="translate(91, 8)">
        <circle r="4.2" fill="white" stroke={INK} strokeWidth="0.25" opacity="0.95" />
        <polygon points="0,-3.2 0.9,0 0,3.2 -0.9,0" fill={MAP_FOOTPATH} />
        <polygon points="0,-3.2 0.9,0 0,0 -0.9,0" fill={INK} />
        <text y="-4.6" textAnchor="middle" fontFamily="'Outfit', sans-serif" fontSize="1.8" fontWeight="900" fill={INK}>N</text>
      </g>

      {/* Scale bar — bottom centre away from the usual start pin */}
      <g transform="translate(42, 63)">
        <rect x="0" y="0" width="10" height="1.2" fill="white" stroke={INK} strokeWidth="0.15" />
        <rect x="0" y="0" width="5" height="1.2" fill={INK} />
        <text x="0" y="3.6" fontFamily="monospace" fontSize="1.6" fill={INK}>0</text>
        <text x="5" y="3.6" textAnchor="middle" fontFamily="monospace" fontSize="1.6" fill={INK}>500m</text>
        <text x="10" y="3.6" textAnchor="end" fontFamily="monospace" fontSize="1.6" fill={INK}>1km</text>
      </g>
    </svg>
  );
}

function LakeTerrain() {
  return (
    <>
      {/* Contour lines hinting at low hills around the lake */}
      <g fill="none" stroke={MAP_CONTOUR} strokeWidth="0.2" opacity="0.55">
        <path d="M 8 50 Q 30 38 52 36 Q 72 36 92 48" />
        <path d="M 14 46 Q 34 34 52 32 Q 70 32 86 42" />
        <path d="M 22 42 Q 38 32 52 30 Q 66 30 78 38" />
      </g>
      {/* The lake itself — organic shape */}
      <path d="M 30 48 Q 26 40 34 36 Q 42 30 55 32 Q 68 32 74 38 Q 80 46 76 54 Q 68 58 58 58 Q 46 58 38 54 Q 30 52 30 48 Z" fill="url(#water-pattern)" stroke={MAP_WATER_DEEP} strokeWidth="0.3" />
      {/* Small island */}
      <ellipse cx="54" cy="44" rx="3" ry="1.6" fill={MAP_GRASS_DARK} stroke={MAP_WATER_DEEP} strokeWidth="0.15" />
      {/* Lake label */}
      <text x="52" y="46" textAnchor="middle" fontFamily="'Outfit', serif" fontStyle="italic" fontSize="2.4" fill={MAP_WATER_DEEP} opacity="0.85">Holborough Lake</text>
      {/* Woodland patches */}
      <path d="M 4 8 Q 18 2 28 6 Q 32 14 26 20 Q 14 22 8 18 Z" fill="url(#wood-pattern)" stroke={MAP_WOOD_DARK} strokeWidth="0.2" opacity="0.95" />
      <path d="M 78 2 Q 92 4 96 14 Q 94 22 86 22 Q 78 18 76 10 Z" fill="url(#wood-pattern)" stroke={MAP_WOOD_DARK} strokeWidth="0.2" opacity="0.95" />
      <path d="M 72 52 Q 82 50 86 56 Q 82 62 74 60 Z" fill="url(#wood-pattern)" stroke={MAP_WOOD_DARK} strokeWidth="0.2" opacity="0.95" />
      {/* Pub marker area */}
      <rect x="86" y="64" width="3" height="3" fill={AMBER} stroke={INK} strokeWidth="0.15" />
      <text x="14" y="14" fontFamily="monospace" fontSize="1.8" fill={MAP_WOOD_DARK} opacity="0.9">Beech Copse</text>
    </>
  );
}

function WoodsTerrain() {
  return (
    <>
      {/* Ridge contours — stacked rising */}
      <g fill="none" stroke={MAP_CONTOUR} strokeWidth="0.25" opacity="0.7">
        <path d="M 0 56 Q 30 50 55 44 Q 75 42 100 48" />
        <path d="M 0 50 Q 28 42 55 36 Q 72 34 100 40" />
        <path d="M 0 44 Q 26 34 55 28 Q 70 26 100 32" />
        <path d="M 10 38 Q 30 26 55 20 Q 70 20 90 26" />
        <path d="M 20 32 Q 40 22 55 18 Q 68 18 80 22" />
      </g>
      {/* Ridge peak marker */}
      <g transform="translate(55, 18)">
        <polygon points="0,-2 1.4,1 -1.4,1" fill={MAP_CONTOUR} stroke={INK} strokeWidth="0.15" />
        <text y="4.5" textAnchor="middle" fontFamily="monospace" fontSize="1.6" fill={INK}>142m</text>
      </g>
      {/* Extensive woodland on north + east */}
      <path d="M 2 4 Q 40 0 62 6 Q 68 12 64 22 Q 40 28 18 26 Q 6 22 2 14 Z" fill="url(#wood-pattern)" stroke={MAP_WOOD_DARK} strokeWidth="0.25" opacity="0.95" />
      <path d="M 72 32 Q 90 28 98 36 Q 100 48 94 52 Q 84 54 74 48 Q 68 40 72 32 Z" fill="url(#wood-pattern)" stroke={MAP_WOOD_DARK} strokeWidth="0.25" opacity="0.95" />
      <text x="30" y="14" fontFamily="monospace" fontSize="2" fill={MAP_WOOD_DARK}>Birchwood</text>
      {/* Stream squiggle */}
      <path d="M -2 62 Q 18 56 32 58 Q 48 62 62 58 Q 78 54 102 58" stroke={MAP_WATER_DEEP} strokeWidth="0.45" fill="none" opacity="0.9" />
    </>
  );
}

function RiverTerrain() {
  return (
    <>
      {/* Flat contours */}
      <g fill="none" stroke={MAP_CONTOUR} strokeWidth="0.2" opacity="0.45">
        <path d="M -2 22 Q 30 20 60 22 Q 85 24 102 22" />
        <path d="M -2 16 Q 30 14 60 16 Q 85 18 102 16" />
        <path d="M -2 58 Q 30 60 60 58 Q 85 58 102 60" />
      </g>
      {/* The river — flows horizontally */}
      <path d="M -2 50 Q 15 46 30 48 Q 48 52 66 48 Q 82 46 102 50 L 102 42 Q 80 38 66 40 Q 48 44 30 40 Q 15 38 -2 42 Z" fill="url(#water-pattern)" stroke={MAP_WATER_DEEP} strokeWidth="0.3" />
      <text x="50" y="56" textAnchor="middle" fontFamily="'Outfit', serif" fontStyle="italic" fontSize="2.4" fill={MAP_WATER_DEEP} opacity="0.85">River Holt</text>
      {/* Mill building */}
      <g transform="translate(89, 42)">
        <rect x="0" y="0" width="4" height="3" fill={AMBER} stroke={INK} strokeWidth="0.15" />
        <polygon points="0,0 2,-1.6 4,0" fill={INK_SOFT} />
        <text y="-2.4" textAnchor="middle" fontFamily="monospace" fontSize="1.5" fill={INK}>Old Mill</text>
      </g>
      {/* Cafe marker at start */}
      <g transform="translate(8, 42)">
        <rect x="0" y="0" width="3" height="2.4" fill={AMBER} stroke={INK} strokeWidth="0.15" />
        <text y="-0.8" fontFamily="monospace" fontSize="1.5" fill={INK}>Cafe</text>
      </g>
      {/* Small woodland patch */}
      <path d="M 34 10 Q 50 8 58 14 Q 54 22 42 22 Q 32 18 34 10 Z" fill="url(#wood-pattern)" stroke={MAP_WOOD_DARK} strokeWidth="0.2" opacity="0.9" />
    </>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border p-3" style={{ borderColor: RULE }}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest opacity-60">
        <Icon size={10} weight="fill" /> {label}
      </div>
      <div className="text-sm font-bold mt-1 truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</div>
    </div>
  );
}

function Badge({ icon: Icon, label, color }) {
  return (
    <div className="text-[11px] font-mono px-2.5 py-1 flex items-center gap-1.5" style={{ backgroundColor: color + "22", color }}>
      <Icon size={12} weight="fill" /> {label}
    </div>
  );
}
